'use client';

import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { logEvent } from '@/lib/analytics';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();
  const configuredRef = useRef(false);

  // Initialize RevenueCat and optionally log in with a user ID
  // Only runs on native Capacitor platforms with a 2-second delay to ensure bridge is ready
  const initRevenueCat = async (userId?: string) => {
    try {
      // Only run on native platform — never on web
      const { Capacitor } = await (Function('return import("@capacitor/core")')() as Promise<any>);
      if (!Capacitor.isNativePlatform()) return;

      // Wait 2 seconds after auth loads to ensure the Capacitor bridge is fully ready
      await new Promise((resolve) => setTimeout(resolve, 2000));

      try {
        const rcMod = await (Function('return import("@revenuecat/purchases-capacitor")')() as Promise<any>);
        if (!rcMod) return;
        const { Purchases } = rcMod;
        if (!Purchases) return;

        const platform = Capacitor.getPlatform(); // 'ios' or 'android'
        const apiKey = platform === 'ios' ?'appl_aOFyKzlROQCBFftzhxcCWvGXMLe' :'goog_dhHRwzmuepYpvbdZujOOZagWAqV';

        if (configuredRef.current) {
          // Already configured — skip configure(), just log in if userId provided
          if (userId) {
            await Purchases.logIn({ appUserID: userId });
          }
          return;
        }

        // Claim synchronously (no await between check and set) so a
        // second concurrent call sees it and skips configure
        configuredRef.current = true;

        try {
          await Purchases.configure({
            apiKey,
            appUserID: userId ?? null,
          });
          if (userId) {
            await Purchases.logIn({ appUserID: userId });
          }
        } catch (configErr) {
          // Release the slot so a later call can retry
          configuredRef.current = false;
          throw configErr;
        }
      } catch (rcErr) {
        console.warn('RevenueCat initialization failed:', rcErr);
      }
    } catch {
      // Capacitor not available — silently ignore on web
    }
  };

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      if (session?.user) {
        // Initialize RevenueCat with the signed-in user ID
        initRevenueCat(session.user.id);

        // Subscription check is handled by appStateChange listener (foreground resume)
        // NOT called here to avoid 401 errors before session cookie propagates
      } else {
        // Initialize RevenueCat without a user ID (anonymous)
        initRevenueCat();
      }
    });

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Fire user_signed_in analytics event on sign-in
      if (session?.user && _event === 'SIGNED_IN') {
        logEvent('user_signed_in');
        // Set RevenueCat app user ID from Supabase user ID after sign in
        initRevenueCat(session.user.id);
        // Ensure user profile exists (handles Google OAuth users whose trigger may have missed)
        ensureUserProfile(session.user);
        // NOTE: checkAndDowngradeIfExpired is intentionally NOT called here.
        // Calling it on SIGNED_IN causes 401 errors because the session cookie
        // has not yet propagated to the server. It is triggered by appStateChange instead.
      }
    });

    // Listen for deep link callbacks on Android (OAuth via Chrome)
    let appUrlListener: { remove: () => void } | null = null;
    const setupAppUrlListener = async () => {
      try {
        const { Capacitor } = await (Function('return import("@capacitor/core")')() as Promise<any>);
        if (!Capacitor.isNativePlatform()) return;

        const { App } = await (Function('return import("@capacitor/app")')() as Promise<typeof import('@capacitor/app')>);
        appUrlListener = await App.addListener('appUrlOpen', async (event: { url: string }) => {
          console.log('[AuthContext] appUrlOpen fired with URL:', event.url);
          if (event.url.startsWith('com.imaanmate.app://auth/callback')) {
            console.log('[AuthContext] Deep link matched — closing Custom Tab and exchanging code');
            // Close the Custom Tab immediately before processing
            try {
              const { Browser } = await import('@capacitor/browser');
              await Browser.close();
              console.log('[AuthContext] Browser.close() called successfully');
            } catch {
              // Fallback to AndroidBridge if @capacitor/browser is unavailable
              if (typeof window !== 'undefined' && window.AndroidBridge?.closeBrowser) {
                window.AndroidBridge.closeBrowser();
              }
            }
            // Exchange the full callback URL for a session
            const { error: sessionError } = await supabase.auth.exchangeCodeForSession(event.url);
            if (sessionError) {
              console.error('[AuthContext] exchangeCodeForSession failed:', sessionError.message);
            } else {
              console.log('[AuthContext] Session exchanged successfully');
            }
          }
        });
      } catch {
        // Not on native platform — silently ignore
      }
    };
    setupAppUrlListener();

    // Listen for app returning to foreground (Capacitor appStateChange)
    // This is the correct place to check subscription expiry — the session is
    // guaranteed to be established when the user manually opens the app.
    let appStateListener: { remove: () => void } | null = null;
    const setupAppStateListener = async () => {
      try {
        const { Capacitor } = await (Function('return import("@capacitor/core")')() as Promise<any>);
        if (!Capacitor.isNativePlatform()) return;

        const { App } = await (Function('return import("@capacitor/app")')() as Promise<typeof import('@capacitor/app')>);
        appStateListener = await App.addListener('appStateChange', async ({ isActive }: { isActive: boolean }) => {
          if (!isActive) return; // App went to background — do nothing
          // App came to foreground — wait 10 seconds to ensure session is fully established
          const sessionData = await supabase.auth.getSession();
          const userId = sessionData.data.session?.user?.id;
          if (!userId) return;
          setTimeout(() => {
            checkAndDowngradeIfExpired(userId);
          }, 10000);
        });
      } catch {
        // Not on native platform — silently ignore
      }
    };
    setupAppStateListener();

    return () => {
      subscription.unsubscribe();
      if (appUrlListener) appUrlListener.remove();
      if (appStateListener) appStateListener.remove();
    };
  }, []);

  // Fallback: check RevenueCat subscription on app open and downgrade if expired
  const checkAndDowngradeIfExpired = async (userId: string) => {
    try {
      const sessionData = await supabase.auth.getSession();
      const accessToken = sessionData.data.session?.access_token;
      const response = await fetch('/api/downgrade', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, accessToken }),
      });
      if (response.ok) {
        const result = await response.json();
        if (result.action === 'downgraded') {
          console.log('Subscription expired — plan reverted to free for user', userId);
        }
      }
    } catch (err) {
      console.error('Subscription fallback check failed:', err);
    }
  };

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '');
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || ''
        },
        emailRedirectTo: `${siteUrl}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    return data;
  };

  // Google Sign In
  const signInWithGoogle = async () => {
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '');

    const isAndroid = typeof window !== 'undefined' && window.isAndroidApp === true;

    if (isAndroid) {
      // Use skipBrowserRedirect + Browser.open to open OAuth in Chrome Custom Tab
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: 'com.imaanmate.app://auth/callback',
          skipBrowserRedirect: true,
        },
      });
      if (error) throw error;
      if (data?.url) {
        console.log('[AuthContext] signInWithGoogle Android — calling Browser.open with URL:', data.url);
        const { Browser } = await import('@capacitor/browser');
        await Browser.open({ url: data.url });
        console.log('[AuthContext] Browser.open() called successfully');
      } else {
        console.warn('[AuthContext] signInWithGoogle Android: no URL returned from Supabase');
      }
      return data;
    }

    // Standard web OAuth flow
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${siteUrl}/auth/callback`,
      }
    });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Ensure user_profiles row exists for the given user (handles OAuth sign-ins)
  const ensureUserProfile = async (authUser: any) => {
    if (!authUser) return;
    const params = {
      p_user_id: authUser.id,
      p_email: authUser.email ?? '',
      p_full_name: authUser.user_metadata?.full_name ?? '',
      p_avatar_url: authUser.user_metadata?.avatar_url ?? '',
    };
    try {
      await supabase.rpc('upsert_user_profile', params);
    } catch (err: any) {
      console.error('[ensureUserProfile] error:', JSON.stringify(err));
      const isConflict =
        err?.code === '409' ||
        err?.status === 409 ||
        err?.message?.toLowerCase().includes('conflict') ||
        err?.message?.toLowerCase().includes('duplicate');
      if (isConflict) {
        console.warn('ensureUserProfile: 409 conflict — retrying after 1s');
        await new Promise((resolve) => setTimeout(resolve, 1000));
        try {
          await supabase.rpc('upsert_user_profile', params);
        } catch (retryErr) {
          console.error('[ensureUserProfile] retry error:', JSON.stringify(retryErr));
          console.warn('ensureUserProfile: retry also failed (non-fatal):', retryErr);
        }
      } else {
        console.warn('ensureUserProfile: upsert failed (non-fatal):', err);
      }
    }
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
